import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Field,
  Input,
  Select,
  useToast,
} from "../../../design-system";
import EmployeePicker from "../../../Components/Kronaly/Empleados/EmployeePicker";
import { uploadInternalDocument } from "../../../helpers/internal_documentation_helper";
import { TEXTAREA_STYLE } from "../constants";
import { levelLabel } from "../helpers";
import { FileDropInput } from "./FileDropInput";

export function UploadDocumentDrawer({
  open,
  onClose,
  levels,
  types,
  parents,
  onUploaded,
}) {
  const toast = useToast();
  const fileRef = useRef(null);
  const initialForm = () => ({
    code: "",
    title: "",
    description: "",
    level_id: "",
    type_id: "",
    parent_document_id: "",
    author_employee_profile_id: "",
    effective_at: "",
    publish: false,
    requires_acknowledgement: false,
  });
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm(initialForm());
      setFile(null);
      setError(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f && !form.title.trim()) {
      update({ title: f.name.replace(/\.[^/.]+$/, "") });
    }
  };

  const onTypeChange = (typeId) => {
    const t = (types || []).find((x) => x.id === typeId);
    update({
      type_id: typeId,
      // Si el tipo tiene nivel por defecto y no se eligió ninguno, lo pre-rellena.
      level_id: form.level_id || (t?.default_level_id || ""),
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) return setError("Selecciona un archivo.");
    if (!form.code.trim()) return setError("El código es obligatorio.");
    if (!form.title.trim()) return setError("El título es obligatorio.");
    if (!form.level_id) return setError("Selecciona un nivel.");
    if (form.publish && !form.effective_at) {
      return setError("Para publicar, indica la fecha de entrada en vigor.");
    }

    setSubmitting(true);
    try {
      await uploadInternalDocument({
        code: form.code.trim(),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        level_id: form.level_id,
        type_id: form.type_id || undefined,
        parent_document_id: form.parent_document_id || undefined,
        author_employee_profile_id: form.author_employee_profile_id || undefined,
        effective_at: form.effective_at || undefined,
        publish: form.publish,
        requires_acknowledgement: form.requires_acknowledgement,
        file,
      });
      toast.success(form.publish ? "Documento publicado" : "Borrador creado");
      onUploaded?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo subir el documento.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "upload-internal-doc-form";

  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Subir documento interno"
      size="md"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" type="submit" form={formId} icon="Upload" disabled={submitting}>
            {submitting ? "Subiendo…" : "Subir documento"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Archivo" required>
          <FileDropInput file={file} inputRef={fileRef} onFileChange={onFileChange} />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
          <Field label="Código" required>
            <Input
              value={form.code}
              onChange={(e) => update({ code: e.target.value })}
              placeholder="Ej. POL-RRHH-001"
              required
            />
          </Field>
          <Field label="Título" required>
            <Input
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Ej. Política de teletrabajo"
              required
            />
          </Field>
        </div>

        <Field label="Descripción">
          <textarea
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Opcional — contexto del documento."
            rows={3}
            style={TEXTAREA_STYLE}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Nivel" required>
            <Select
              value={form.level_id}
              onChange={(e) => update({ level_id: e.target.value })}
            >
              <option value="">— Selecciona —</option>
              {(levels || []).map((l) => (
                <option key={l.id} value={l.id}>
                  {levelLabel(l)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tipo">
            <Select
              value={form.type_id}
              onChange={(e) => onTypeChange(e.target.value)}
            >
              <option value="">— Ninguno —</option>
              {(types || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Entrada en vigor">
            <Input
              type="date"
              value={form.effective_at}
              onChange={(e) => update({ effective_at: e.target.value })}
            />
          </Field>
          <Field label="Documento padre" help="Cuelga este doc de otro existente.">
            <Select
              value={form.parent_document_id}
              onChange={(e) => update({ parent_document_id: e.target.value })}
            >
              <option value="">— Ninguno (raíz) —</option>
              {(parents || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} · {p.title}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Autor designado" help="Empleado responsable del contenido (opcional).">
          <EmployeePicker
            value={form.author_employee_profile_id}
            onChange={(id) => update({ author_employee_profile_id: id || "" })}
            allowEmpty
          />
        </Field>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--kr-fg-strong)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={form.publish}
            onChange={(e) => update({ publish: e.target.checked })}
          />
          Publicar inmediatamente (requiere fecha de entrada en vigor)
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 13,
            color: "var(--kr-fg-strong)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={form.requires_acknowledgement}
            onChange={(e) => update({ requires_acknowledgement: e.target.checked })}
            style={{ marginTop: 2 }}
          />
          <span>
            Requiere lectura obligatoria
            <span
              style={{
                display: "block",
                color: "var(--kr-fg-muted)",
                fontSize: 12,
                marginTop: 2,
              }}
            >
              Al publicar, todos los empleados activos verán este documento como
              pendiente hasta marcarlo como leído.
            </span>
          </span>
        </label>
      </form>
    </Drawer>
  );
}
